import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import paymentService from '../../services/paymentService';
import LoadingSpinner from '../UI/LoadingSpinner';
import './PaymentFailed.css';

const PaymentSbiFailed = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [paymentDetails, setPaymentDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [retrying, setRetrying] = useState(false);

  useEffect(() => {
    const processPaymentFailure = async () => {
      try {
        // Get payment details from URL parameters
        const urlParams = new URLSearchParams(location.search);
        const orderId = urlParams.get('orderId');
        const error = urlParams.get('error');
        const status = urlParams.get('status');

        if (!orderId) {
          throw new Error('Invalid payment information');
        }

        // Set initial payment details from URL
        const initialDetails = {
          orderId,
          error: error || 'Payment processing failed',
          status: status || 'failed',
          timestamp: new Date().toISOString()
        };

        setPaymentDetails(initialDetails);

        // Try to fetch complete payment details from backend
        try {
          const response = await paymentService.getPayment(orderId);
          if (response.success) {
            setPaymentDetails(prev => ({
              ...response.data,
              error: error || response.data.gatewayResponseMessage || 'Payment processing failed'
            }));
          }
        } catch (fetchError) {
          console.warn('Could not fetch complete payment details:', fetchError);
          // Continue with URL parameters if backend fetch fails
        }

        // Clear any existing payment session
        paymentService.clearPaymentSession();

        setLoading(false);

      } catch (err) {
        console.error('Payment failure processing error:', err);
        setPaymentDetails({
          error: err.message,
          status: 'failed',
          timestamp: new Date().toISOString()
        });
        setLoading(false);
      }
    };

    processPaymentFailure();
  }, [location]);

  useEffect(() => {
    const timer = setTimeout(() => {
      navigate('/');
    }, 3000);
    
    return () => clearTimeout(timer);
  }, [navigate]);
  
  const handleRetryPayment = async () => {
    if (!paymentDetails) return;

    setRetrying(true);
    
    try {
      // Navigate back to payment form with pre-filled data
      const paymentData = {
        amount: paymentDetails.amount,
        customerEmail: paymentDetails.customerEmail,
        customerPhone: paymentDetails.customerPhone,
        customerName: paymentDetails.customerName,
        description: paymentDetails.description
      };

      // Store data for payment form
      sessionStorage.setItem('retryPaymentData', JSON.stringify(paymentData));
      
      // Navigate to payment form
      navigate('/payment', { 
        state: { 
          retryData: paymentData,
          previousOrderId: paymentDetails.orderId 
        } 
      });

    } catch (error) {
      console.error('Retry payment error:', error);
      setRetrying(false);
    }
  };

  const handleContactSupport = () => {
    // You can customize this based on your support system
    const subject = `Payment Failed - Order ${paymentDetails?.orderId}`;
    const body = `Hello,

I encountered an issue with my payment:

Order ID: ${paymentDetails?.orderId || 'N/A'}
Error: ${paymentDetails?.error || 'Unknown error'}
Amount: ${paymentDetails?.amount ? paymentService.formatCurrency(paymentDetails.amount) : 'N/A'}
Date: ${new Date().toLocaleString()}

Please help me resolve this issue.

Thank you.`;

    const mailtoLink = `mailto:support@yourcompany.com?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    window.location.href = mailtoLink;
  };

  const handleGoHome = () => {
    navigate('/');
  };

  const handleViewPayments = () => {
    navigate('/payments/history');
  };

  if (loading) {
    return (
      <div className="payment-failed-container">
        <div className="payment-failed-card">
          <LoadingSpinner />
          <p>Processing payment information...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="payment-failed-container">
      <div className="payment-failed-card">
        {/* Failure Animation */}
        <div className="failure-animation">
          <div className="error-circle">
            <div className="error-mark">✕</div>
          </div>
        </div>

        {/* Failure Message */}
        <div className="failure-content">
          <h1>Payment Failed</h1>
          <p className="failure-message">
            Unfortunately, your payment could not be processed. Please try again or contact our support team.
          </p>
        </div>

        {/* Error Details */}
        <div className="error-details">
          <h3>Error Information</h3>
          <div className="error-box">
            <div className="error-icon">⚠️</div>
            <div className="error-text">
              <strong>Error:</strong>
              <p>{paymentDetails?.error || 'Payment processing failed'}</p>
            </div>
          </div>
        </div>

        {/* Payment Details */}
        {paymentDetails && (
          <div className="payment-details">
            <h3>Payment Details</h3>
            <div className="details-grid">
              {paymentDetails.orderId && (
                <div className="detail-item">
                  <span className="label">Order ID:</span>
                  <span className="value">{paymentDetails.orderId}</span>
                </div>
              )}
              
              {paymentDetails.amount && (
                <div className="detail-item">
                  <span className="label">Amount:</span>
                  <span className="value amount">
                    {paymentService.formatCurrency(paymentDetails.amount)}
                  </span>
                </div>
              )}
              
              <div className="detail-item">
                <span className="label">Status:</span>
                <span className={`value status ${paymentDetails.status}`}>
                  {paymentService.getStatusIcon(paymentDetails.status)} 
                  {paymentDetails.status?.toUpperCase()}
                </span>
              </div>
              
              <div className="detail-item">
                <span className="label">Date & Time:</span>
                <span className="value">
                  {new Date(paymentDetails.timestamp).toLocaleString()}
                </span>
              </div>

              {paymentDetails.gatewayResponseCode && (
                <div className="detail-item">
                  <span className="label">Error Code:</span>
                  <span className="value">{paymentDetails.gatewayResponseCode}</span>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Action Buttons */}
        <div className="actions">
          {/* <button 
            onClick={handleRetryPayment}
            className="btn btn-primary"
            disabled={retrying}
          >
            {retrying ? (
              <>
                <LoadingSpinner size="small" />
                Retrying...
              </>
            ) : (
              <>
                🔄 Try Again
              </>
            )}
          </button> */}
          
          {/* <button 
            onClick={handleContactSupport}
            className="btn btn-secondary"
          >
            📞 Contact Support
          </button>
          
          <button 
            onClick={handleViewPayments}
            className="btn btn-outline"
          >
            📋 View Payments
          </button>
          
          <button 
            onClick={handleGoHome}
            className="btn btn-outline"
          >
            🏠 Go to Home
          </button> */}
        </div>
     

        {/* Footer */}
        <div className="failure-footer">
          <p>
            Don't worry, no money has been charged to your account. 
            {/* <Link to="/faq" className="faq-link">Check our FAQ</Link> for more help. */}
          </p>
        </div>
      </div>
    </div>
  );
};

export default PaymentSbiFailed;